import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const SECTIONS = [
  {
    title: "Société",
    fields: [
      ["company_name", "Nom de l'entreprise"],
      ["company_phone", "Téléphone affiché"],
      ["company_phone_raw", "Téléphone brut (tel:)"],
      ["company_whatsapp", "WhatsApp (international)"],
      ["company_email", "Email"],
      ["company_address", "Adresse"],
      ["company_siret", "SIRET"],
      ["company_tva_intra", "TVA intracommunautaire"],
    ],
  },
  {
    title: "Hero (page d'accueil)",
    fields: [
      ["hero_title", "Titre principal"],
      ["hero_subtitle", "Sous-titre", "textarea"],
      ["hero_cta_primary", "Libellé CTA principal"],
      ["hero_cta_secondary", "Libellé CTA secondaire"],
    ],
  },
  {
    title: "KPIs / promesses",
    fields: [
      ["kpi_intervention_min", "Délai d'intervention (min)"],
      ["kpi_couverture", "Couverture (zone)"],
      ["kpi_disponibilite", "Disponibilité"],
    ],
  },
  {
    title: "Charte couleurs",
    fields: [
      ["brand_color_primary", "Couleur principale (#hex)"],
      ["brand_color_accent", "Couleur accent (#hex)"],
      ["brand_color_background", "Couleur de fond (#hex)"],
    ],
  },
  {
    title: "Réseaux sociaux",
    fields: [
      ["social_facebook", "Facebook (URL)"],
      ["social_instagram", "Instagram (URL)"],
      ["social_linkedin", "LinkedIn (URL)"],
      ["social_tiktok", "TikTok (URL)"],
    ],
  },
  {
    title: "SEO par défaut",
    fields: [
      ["default_meta_title", "Meta title par défaut"],
      ["default_meta_description", "Meta description par défaut", "textarea"],
    ],
  },
  {
    title: "Pied de page",
    fields: [
      ["footer_about", "Bloc « À propos »", "textarea"],
      ["footer_baseline", "Baseline"],
    ],
  },
];

export default function AdminSettings() {
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setT(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      SECTIONS.forEach((s) => s.fields.forEach(([k]) => { payload[k] = t[k] ?? ""; }));
      const r = await api.patch("/admin/settings", payload);
      setT(r.data); toast.success("Paramètres mis à jour");
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  if (!t) return <div className="text-zinc-400">Chargement…</div>;

  return (
    <div>
      <div className="font-heading text-3xl font-black uppercase tracking-tight">Paramètres</div>
      <div className="h-1 w-16 bg-brand mt-2 mb-6" />

      <div className="grid gap-6 max-w-4xl">
        {SECTIONS.map((sec) => (
          <div key={sec.title} className="bg-ink-900 border border-ink-700 p-5">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-4">{sec.title}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {sec.fields.map(([k, label, kind]) => (
                <label key={k} className={`block ${kind === "textarea" ? "sm:col-span-2" : ""}`}>
                  <span className="text-xs uppercase tracking-widest text-zinc-500">{label}</span>
                  {kind === "textarea" ? (
                    <textarea
                      className="mt-1 w-full px-3 py-2 bg-ink-800 border border-ink-700 focus:border-brand text-white outline-none"
                      rows={3}
                      value={t[k] || ""}
                      onChange={(e) => setT((s) => ({ ...s, [k]: e.target.value }))}
                      data-testid={`setting-${k}`}
                    />
                  ) : (
                    <input
                      className="mt-1 w-full h-11 px-3 bg-ink-800 border border-ink-700 focus:border-brand text-white outline-none"
                      value={t[k] || ""}
                      onChange={(e) => setT((s) => ({ ...s, [k]: e.target.value }))}
                      data-testid={`setting-${k}`}
                    />
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} className="mt-6 bg-brand hover:bg-brand-600 text-white font-heading font-black uppercase px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50 transition-colors" data-testid="settings-save">
        <Save size={16} /> Enregistrer
      </button>
      <p className="text-zinc-500 text-xs mt-3">Toutes les pages publiques (header, footer, hero, SEO) se mettent à jour automatiquement.</p>
    </div>
  );
}
